import { useState, useEffect } from 'react';

export function useDeviceDetect() {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  useEffect(() => {
    // Function to check if device is mobile
    const checkMobile = () => {
      // Check using window width - Common breakpoint for mobile devices
      const mobileByWidth = window.innerWidth <= 768;
      
      // Also check using user agent for better detection
      const mobileByUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Combine both checks - either condition can indicate a mobile device
      setIsMobile(mobileByWidth || mobileByUserAgent);
    };

    // Function to set dynamic positioning based on viewport
    const setDynamicPositioning = () => {
      const vh = window.innerHeight;
      
      // Calculate safe area insets (for newer phones with notches/home indicators)
      const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0');
      
      // Base footer height estimate (py-4 = 32px + content ≈ 20px + links ≈ 20px = ~72px)
      const footerHeight = 72;
      
      // Calculate dynamic values based on screen height
      let timerOverlayBottom, timerActiveBottom, contentPaddingOverlay, contentPaddingActive;
      
      if (vh <= 600) {
        // Very small screens (older iPhones, small Android)
        timerOverlayBottom = footerHeight + 20;
        timerActiveBottom = footerHeight + 15;
        contentPaddingOverlay = timerOverlayBottom + 160;
        contentPaddingActive = timerActiveBottom + 100;
      } else if (vh <= 700) {
        // Medium-small screens
        timerOverlayBottom = footerHeight + 30;
        timerActiveBottom = footerHeight + 20;
        contentPaddingOverlay = timerOverlayBottom + 180;
        contentPaddingActive = timerActiveBottom + 120;
      } else if (vh <= 800) {
        // Medium screens (most phones)
        timerOverlayBottom = footerHeight + 40;
        timerActiveBottom = footerHeight + 30;
        contentPaddingOverlay = timerOverlayBottom + 200;
        contentPaddingActive = timerActiveBottom + 140;
      } else if (vh <= 900) {
        // Large screens
        timerOverlayBottom = footerHeight + 50;
        timerActiveBottom = footerHeight + 40;
        contentPaddingOverlay = timerOverlayBottom + 220;
        contentPaddingActive = timerActiveBottom + 160;
      } else {
        // Very large screens (tablets in portrait, large phones)
        timerOverlayBottom = footerHeight + 60;
        timerActiveBottom = footerHeight + 50;
        contentPaddingOverlay = timerOverlayBottom + 240;
        contentPaddingActive = timerActiveBottom + 180;
      }

      // Add safe area bottom for devices with home indicators
      timerOverlayBottom += safeAreaBottom;
      timerActiveBottom += safeAreaBottom;
      contentPaddingOverlay += safeAreaBottom;
      contentPaddingActive += safeAreaBottom;

      // Set CSS custom properties
      document.documentElement.style.setProperty('--timer-overlay-bottom', `${timerOverlayBottom}px`);
      document.documentElement.style.setProperty('--timer-active-bottom', `${timerActiveBottom}px`);
      document.documentElement.style.setProperty('--content-padding-overlay', `${contentPaddingOverlay}px`);
      document.documentElement.style.setProperty('--content-padding-active', `${contentPaddingActive}px`);
      
      // Set viewport height as data attribute for debugging
      document.body.setAttribute('data-vh', vh.toString());
    };

    // Function to handle both mobile check and positioning
    const handleResize = () => {
      checkMobile();
      setDynamicPositioning();
    };
    
    // Initial check
    handleResize();
    
    // Add resize listener to update on orientation change or window resize
    window.addEventListener('resize', handleResize);
    
    // Also listen for orientation change specifically
    window.addEventListener('orientationchange', () => {
      // Add small delay for orientation change to complete
      setTimeout(handleResize, 100);
    });
    
    // Cleanup function to remove listeners
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  return { isMobile };
} 